import type { OrderStatus } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";

export type TransitionVariant = "primary" | "danger";

export type OrderTransition = {
  readonly targetStatus: OrderStatus;
  readonly labelKey: string;
  readonly variant: TransitionVariant;
  readonly needsEta: boolean;
  readonly requiresDeclineReason: boolean;
};

/**
 * Source of truth for seller-facing primary transitions from a given order
 * status. Mirrors the backend's ALLOWED_TRANSITIONS policy but narrows it to
 * the single "forward" move plus the universal decline action — the
 * advanced dropdown remains the escape hatch for any other valid target.
 *
 * Keep the target list in sync with
 * `apps/backend/src/modules/order/service/policies/order-status-transitions.policy.ts`.
 */
const TRANSITIONS: Record<OrderStatus, readonly OrderTransition[]> = {
  draft: [],
  // Buyer verification is normally cleared by the buyer's confirmation (e.g.
  // the WhatsApp "Confirm" tap). Confirm is the seller's manual escape hatch so
  // the order is never stuck; cancel remains via the advanced dropdown.
  pending_buyer_verification: [
    {
      targetStatus: "confirmed",
      labelKey: keys.seller.orderActionConfirm,
      variant: "primary",
      needsEta: false,
      requiresDeclineReason: false,
    },
  ],
  // The buyer hasn't uploaded a payment proof yet. Backend disallows
  // `pending_buyer_payment → confirmed` (the transition policy routes
  // online-payment orders through pending_payment_verification once the
  // proof lands). The seller's only forward lever here is decline.
  pending_buyer_payment: [
    {
      targetStatus: "declined",
      labelKey: keys.seller.orderActionDecline,
      variant: "danger",
      needsEta: false,
      requiresDeclineReason: true,
    },
  ],
  pending_payment_verification: [
    {
      targetStatus: "confirmed",
      labelKey: keys.seller.orderActionConfirm,
      variant: "primary",
      needsEta: false,
      requiresDeclineReason: false,
    },
    {
      targetStatus: "declined",
      labelKey: keys.seller.orderActionDecline,
      variant: "danger",
      needsEta: false,
      requiresDeclineReason: true,
    },
  ],
  paid_less_than_required: [
    {
      targetStatus: "confirmed",
      labelKey: keys.seller.orderActionConfirm,
      variant: "primary",
      needsEta: false,
      requiresDeclineReason: false,
    },
    {
      targetStatus: "declined",
      labelKey: keys.seller.orderActionDecline,
      variant: "danger",
      needsEta: false,
      requiresDeclineReason: true,
    },
  ],
  // COD order placed while the store's autoConfirmCodOrders is OFF — awaits
  // the seller's manual confirmation. Confirm is the forward move; decline is
  // the universal escape. (Cancel remains available via the advanced dropdown,
  // see ALL_ALLOWED_TARGETS.)
  pending_seller_confirmation: [
    {
      targetStatus: "confirmed",
      labelKey: keys.seller.orderActionConfirm,
      variant: "primary",
      needsEta: false,
      requiresDeclineReason: false,
    },
    {
      targetStatus: "declined",
      labelKey: keys.seller.orderActionDecline,
      variant: "danger",
      needsEta: false,
      requiresDeclineReason: true,
    },
  ],
  // `confirmed → preparing` is a simple "I started preparing" signal.
  // Forcing an ETA here is friction — the seller usually doesn't know the
  // courier horizon yet. ETA is asked at the ship step instead. Decline is
  // not a valid backend transition from confirmed (see backend
  // `ALLOWED_TRANSITIONS`), so we only surface the forward action.
  confirmed: [
    {
      targetStatus: "preparing",
      labelKey: keys.seller.orderActionPreparing,
      variant: "primary",
      needsEta: false,
      requiresDeclineReason: false,
    },
  ],
  // `preparing → shipped` is the real courier commitment — ETA required.
  // Decline also not allowed by the backend from preparing.
  preparing: [
    {
      targetStatus: "shipped",
      labelKey: keys.seller.orderActionShipped,
      variant: "primary",
      needsEta: true,
      requiresDeclineReason: false,
    },
  ],
  shipped: [
    {
      targetStatus: "delivered",
      labelKey: keys.seller.orderActionDelivered,
      variant: "primary",
      needsEta: false,
      requiresDeclineReason: false,
    },
  ],
  delivered: [],
  cancelled: [],
  declined: [],
};

export function getAllowedTransitions(
  status: OrderStatus,
): readonly OrderTransition[] {
  return TRANSITIONS[status] ?? [];
}

/**
 * Complete set of targets the backend's `ALLOWED_TRANSITIONS` policy
 * permits from a given status. Used by the advanced dropdown so sellers
 * can't pick an invalid target that would 400. Keep this in sync with
 * `apps/backend/src/modules/order/service/policies/order-status-transitions.policy.ts`.
 */
const ALL_ALLOWED_TARGETS: Record<OrderStatus, readonly OrderStatus[]> = {
  draft: [],
  paid_less_than_required: [
    "pending_payment_verification",
    "confirmed",
    "declined",
    "cancelled",
  ],
  pending_buyer_verification: ["confirmed", "cancelled"],
  pending_buyer_payment: [
    "pending_payment_verification",
    "cancelled",
    "declined",
  ],
  pending_payment_verification: ["confirmed", "declined", "cancelled"],
  pending_seller_confirmation: ["confirmed", "declined", "cancelled"],
  confirmed: ["preparing", "cancelled"],
  preparing: ["shipped", "cancelled"],
  shipped: ["delivered", "cancelled"],
  delivered: [],
  cancelled: [],
  declined: [],
};

export function getAllValidTargets(
  status: OrderStatus,
): readonly OrderStatus[] {
  return ALL_ALLOWED_TARGETS[status] ?? [];
}
